import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { IPanelConfiguration } from '@ispm/therapies/interfaces/section-configuration.interface';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import { Observable } from 'rxjs/Observable';
import { IViewConfiguration } from '@ispm/therapies/interfaces/view-configuration.interface';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { IWorkflowRequest } from '@ispm/workflow/interfaces/workflow-request.interface';
import { Workflow } from '@ispm/core/interfaces/workflow.interface';
import { concatMap, distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { Queries } from '@ispm/shared/utils/Queries';
import { QueryTerm } from '@ispm/shared/classes/query-term';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { Subject} from 'rxjs';
import { PanelPosition } from '@ispm/layout/components/panel/panel.component';
import { TreatmentSelectionService } from '@ispm/therapies/services/treatment-selection.service';
import { ISubjectIdentifier } from "@ispm/therapies/interfaces/subject.interface";
import { ConfigurationStoreHandler } from "../../../configuration/services/configuration-store-handler.service";
import { combineLatest } from 'rxjs/observable/combineLatest';
import { IDefinition } from "../../../configuration/interfaces/definition.interface";
import { RESOURCE_TYPE_NAME } from "@ispm/therapies/constants/default-clinical-recommendation-sorting-configuration.constant";

interface ActionsStatus {
  signoff: boolean;
  edit: boolean;
  showInReportToggleActive?: boolean;
  showInMIToggleActive?: boolean;
}

@Component({
  selector: ' gen-treatment-view',
  templateUrl: './treatment-view.component.html',
  styleUrls: ['./treatment-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreatmentViewComponent implements OnInit, OnDestroy {
  public ModalLoaderTunnelName: string = TherapiesService.ModalLoaderTunnelName;
  public editStatuses$: Observable<ActionsStatus>;
  public reflexMessage$: Observable<any>;
  public verticalLeftContainerPatientView = false;

  public subjectIdentifier$: Subject<ISubjectIdentifier> = new Subject<ISubjectIdentifier>();
  public dataFromLeftPanel$: Subject<any> = new Subject<any>();
  private destroyer$: Subject<void> = new Subject<void>();
  private isActiveRevision: boolean;
  private isPrioritizeStep: boolean = false;

  @Input()
  public resourcesMap$: Observable<{ [name in RESOURCE_TYPE_NAME]?: any[]}>;

  @Input()
  workflowId: string;
  @Input()
  revisionId: string;

  @Input()
  public isPerformer: boolean;

  @Input()
  public changeOpinionOfMIIn:boolean;

  public subjectIdPath: string;
  public position: PanelPosition;

  public testConfig$: Observable<IDefinition>;
  public viewConfig$: Observable<IViewConfiguration>;

  public hideNavigationPanel: boolean;
  public editStatuses: ActionsStatus;
  public isSpinnerActive$: Observable<boolean>;

  constructor(
    private therapiesService: TherapiesService,
    private workflowAC: WorkflowConfigService,
    private userAC: UserActionCreatorService,
    private therapiesAC: TherapiesActionCreatorService,
    private _changeDetector: ChangeDetectorRef,
    private treatmentService: TreatmentSelectionService,
    private configurationHandler: ConfigurationStoreHandler,
  ) {
    this.treatmentService.setSpinnerActive(true);
    this.isSpinnerActive$ = this.treatmentService.isSpinnerActive$;
  }

  ngOnInit() {
    this.reflexMessage$ = this.therapiesService.getReflexMessageForUi(this.revisionId);

    this.testConfig$ = this.configurationHandler.getCurrentConfig().pipe(
      filter(testConfig => !!testConfig)
    );
    this.viewConfig$ = this.therapiesAC.getViewConfig().pipe(
      filter(viewConfig => !!viewConfig),
    );

    this.setPriorityAvailable();
  }

  ngOnDestroy(): void {
    this.subjectIdentifier$.next();
    this.subjectIdentifier$.complete();
    this.destroyer$.next();
    this.destroyer$.complete();
    this.therapiesService.destroyModal(this.ModalLoaderTunnelName, false);
  }

  changeOpinionOfMI(event) {
    this.changeOpinionOfMIIn = event;
  }

  private setPriorityAvailable() {
    combineLatest([
      this.workflowAC.getCurrentActiveWorkflow(),
      this.viewConfig$,
    ]).pipe(
      take(1),
      map(([workflowRequest, viewConfig]) => {
        this.therapiesAC.setGlobalConfigurations(viewConfig.globals);
        const revision = Workflow.getRevision(workflowRequest.revisions, this.revisionId);
        let tmpInput, requiredReviews, signOffRole, amountOfReviewsAllowed;
        this.isActiveRevision = workflowRequest.activeRevision === this.revisionId;

        if (this.isActiveRevision && revision) {
          const currentStep = Workflow.getCurrentStep(revision, workflowRequest.activeJob);

          tmpInput = Workflow.getInput(currentStep.inputs, 'requiredReviews');
          requiredReviews = !!tmpInput && tmpInput.value ? <string> tmpInput.value : 0;
          TherapiesService.REQUIRED_REVIEWS = parseInt(<string> requiredReviews);

          tmpInput = Workflow.getInput(currentStep.inputs, 'signOffRole');
          signOffRole = !!tmpInput && tmpInput.value ? tmpInput.value.toLowerCase() : 'pathologist';

          tmpInput = Workflow.getInput(currentStep.inputs, 'amountOfReviewsAllowed');
          amountOfReviewsAllowed = !!tmpInput && tmpInput.value ? tmpInput.value : '-1';

          tmpInput = Workflow.getInput(currentStep.inputs, 'priorityAvailable');
          this.isPrioritizeStep = tmpInput && tmpInput.value !== 'false';

          if (this.isPrioritizeStep) {
            tmpInput = Workflow.getInput(currentStep.inputs, 'selection');

            if (tmpInput) {
              this.setSelectablesDrugs(viewConfig, JSON.parse(tmpInput.value));
            }
          }
        }
        return { amountOfReviewsAllowed, workflowRequest, requiredReviews, signOffRole};
      }),
      concatMap(({ amountOfReviewsAllowed, workflowRequest, requiredReviews, signOffRole}) =>
        this.setIsEditable(amountOfReviewsAllowed, workflowRequest, requiredReviews, signOffRole)
      ),
      takeUntil(this.destroyer$.asObservable())
    ).subscribe();
  }

  private setSelectablesDrugs(viewConfig: IViewConfiguration, selectionMap: { within: string, outside: string, investigation: string}) {
    const relevantTab = viewConfig.information_section.tabs.find(tab => {
      return !!tab.panels.find(panel => panel.path === 'drugs');
    });

    function findTreatmentLevel(panel: IPanelConfiguration, treatmentLevelPath: string) {
      return panel.conditions.find(cond => cond.path === treatmentLevelPath && cond.value === true);
    }

    if (relevantTab) {
      relevantTab.panels.forEach(panel => {
        if (panel.path === 'drugs') {
          if (!!findTreatmentLevel(panel, 'fdaApprovedForIndication')) {
            panel.selectable = JSON.parse(selectionMap.within);
          } else if (!!findTreatmentLevel(panel, 'fdaApprovedOutsideIndication')) {
            panel.selectable = JSON.parse(selectionMap.outside);
          } else if (!!findTreatmentLevel(panel, 'investigationalForIndication')) {
            panel.selectable = JSON.parse(selectionMap.investigation);
          }
        }
      });
    }
  }

  private setIsEditable(amountOfReviewsAllowed: number, currentWorkflow: IWorkflowRequest, requiredReviews: number, signOffRole: string) {
    return this.userAC.getCurrentUser().pipe(
      take(1),
      concatMap(({ id, role}) => this.getIsEditableObservable(id, role, amountOfReviewsAllowed, requiredReviews, signOffRole, currentWorkflow)),
      tap(status => {
        this.editStatuses = status;
        this.treatmentService.setSpinnerActive(false);
      }),
    );
  }

  private getIsEditableObservable(
    userId: string,
    userRole: string,
    amountOfReviewsAllowed: number,
    requiredReviews: number,
    signOffRole: string,
    currentWorkflow: IWorkflowRequest
  ): Observable<ActionsStatus> {
    const query = [new QueryTerm('resourceType', 'Practitioner'), new QueryTerm('reference', userId)];
    return this.therapiesAC.getReviews()
      .pipe(
        filter(reviews => !!reviews),
        distinctUntilChanged(),
        tap(() => this.therapiesAC.initUserReviewState(this.revisionId)),
        map((reviewsResponse: Array<any>) => {
          const userPassReviewLimit = (amountOfReviewsAllowed != -1
            && reviewsResponse.filter(review => Queries.findByProperty(review, 'context', query)).length > amountOfReviewsAllowed);
          let isLastReview = false;

          if (reviewsResponse.length > 0) {
            isLastReview = reviewsResponse[reviewsResponse.length - 1]['context']
              .find(context => Queries.findByProperty(context, 'resourceType', 'Practitioner')).reference === userId;
          }

          // Is one of the final\completed steps
          const isFinalStep = this.isActiveRevision && Workflow.checkCurrentJob(currentWorkflow, ['signoff', 'final review', 'test completed', 'none']);

          // Is passed completed steps
          const isNotCompleted = this.isActiveRevision &&  !Workflow.checkCurrentJob(currentWorkflow, ['test completed', 'none']);

          // Is allow to edit treatment selection
          const editAllowed = this.isActiveRevision && this.isPerformer && this.isPrioritizeStep && isNotCompleted;

          // Is allow to perform signoff
          const signOffAllowed = this.isPerformer
            && this.isActiveRevision
            && !isFinalStep
            && isLastReview
            && (signOffRole.toLowerCase() === userRole.toLowerCase() || userRole.toLowerCase() === 'oncologist role for genomics app')
            && reviewsResponse.length >= requiredReviews;

          const permittedActions = {
            signoff: this.isAdminHasPermissionToSignOff() ? true : signOffAllowed,
            edit: editAllowed && !userPassReviewLimit,
            includeEdit: isNotCompleted,
            showInReportToggleActive: isNotCompleted
          };

          this.therapiesAC.setPermittedActions(permittedActions);

          return permittedActions;
        }));
  }

  private isAdminHasPermissionToSignOff() {
    let user = JSON.parse(localStorage.getItem('user'));
    return user.isAdmin && (user.permissions.includes('ADMIN.ORGANIZATION.WORKLIST')
      && user.permissions.includes('ADMIN.ORGANIZATION.WORKLIST.CREATE'));
  }

  onSubjectPathChange(path: string) {
    this.subjectIdPath = path;
  }

  onSubjectChange(subjectIdentifier: ISubjectIdentifier) {
    this.subjectIdentifier$.next(subjectIdentifier);
  }

  onDataReceived(data: any) {
    this.dataFromLeftPanel$.next(data);
    this._changeDetector.markForCheck();
  }
}
